import { useRef } from 'react';
import { LiveBadge } from '@components/live/LiveBadge';
import { pollShare } from '@components/polls/pollFormat';
import type { LiveQuestion, LiveState } from '@models/api';
import { liveLink, liveLinkDisplay } from '@lib/livePolls/liveLinks';
import { useFitLevel } from '@lib/livePolls/useFitLevel';
import { cn } from '@lib/utils';
import { LIVE_POLL_COPY } from '../livePollCopy';
import { BigScreenQr, PlaceLogo, ReconnectingChip, ShareBar, TypedLink } from './BigScreenParts';
import { useStageBox } from './BigScreenStage';

/** Answers go one above the other, as in the app (founder, 3 Nov). Up to this many they always do. */
const ALWAYS_ONE_COLUMN_MAX_OPTIONS = 4;

// Denser steps, tried in order only while the screen overflows (index 0 = the frame). A question is never cut
// (up to 200 characters, FE-L2a review): it wraps, and the levels make room around it. `side`: the Live pill and
// the count line move into the QR column, which frees the room they take above and below the bars.
// B2: the question scales down from 56px to its 40px floor; answers stay 28px.
// eslint-disable-next-line react-refresh/only-export-components -- the levels, read by the floor test
export const B2_LEVELS = [
  {
    root: 'gap-9 pt-14 pb-12',
    q: 'text-[56px] leading-[62px]',
    bars: 'gap-3.5',
    row: 'min-h-16 py-1',
    side: false,
  },
  {
    root: 'gap-7 pt-12 pb-10',
    q: 'text-[48px] leading-[54px]',
    bars: 'gap-2.5',
    row: 'min-h-14 py-1',
    side: false,
  },
  {
    root: 'gap-5 pt-10 pb-8',
    q: 'text-[40px] leading-[46px]',
    bars: 'gap-2',
    row: 'min-h-[52px] py-0.5',
    side: false,
  },
  {
    root: 'gap-4 pt-8 pb-6',
    q: 'text-[40px] leading-[44px]',
    bars: 'gap-1.5',
    row: 'min-h-12 py-0.5',
    side: true,
  },
] as const;

// B2c: answers 24px (their floor: readable across a room), wrapping, never clipped; the question 40px. The bars
// run 896px wide (frame: 828) with the QR column at the right edge's 40px, so 80 characters take 3 lines.
// Loop B 867: the frame's values (level 0) hold as long as they can. The first steps give up only the Live pill's
// row (it joins the QR column) and the outer spacing; rows keep 6/20 gap 16 and answers 24/28 until the last
// level. The count line stays at the bottom (RESOLUTIONS L14) at every level but the last: the worst case (a
// 200-character question, 10 answers of 80) needs the last level's room, rows 0/12 and answers 24/25 with the
// count moved into the QR column, or it runs off a 720-high stage (LOOPB-867 review).
// eslint-disable-next-line react-refresh/only-export-components -- the levels, read by the floor test
export const B2C_LEVELS = [
  {
    root: 'gap-[18px] pt-8 pb-7',
    q: 'leading-[46px]',
    col: 'gap-1.5',
    row: 'min-h-14 gap-4 px-5 py-1.5',
    text: 'leading-7',
    side: false,
    countSide: false,
  },
  {
    root: 'gap-[18px] pt-8 pb-7',
    q: 'leading-[46px]',
    col: 'gap-1.5',
    row: 'min-h-14 gap-4 px-5 py-1.5',
    text: 'leading-7',
    side: true,
    countSide: false,
  },
  {
    root: 'gap-3 pt-6 pb-5',
    q: 'leading-[44px]',
    col: 'gap-1.5',
    row: 'min-h-14 gap-4 px-5 py-1.5',
    text: 'leading-7',
    side: true,
    countSide: false,
  },
  {
    root: 'gap-3 pt-6 pb-5',
    q: 'leading-[44px]',
    col: 'gap-1',
    row: 'min-h-12 gap-4 px-5 py-1',
    text: 'leading-7',
    side: true,
    countSide: false,
  },
  {
    root: 'gap-2 pt-4 pb-3',
    q: 'leading-[44px]',
    col: 'gap-1',
    row: 'min-h-10 gap-2 px-3 py-0',
    text: 'leading-[25px]',
    side: true,
    countSide: true,
  },
] as const;

/**
 * The order the screen tries its layouts in, stopping at the first that fits the stage (`useFitLevel` measures):
 * B2's levels (one column, answers 28px), B2c's (one column, answers 24px, the wider bars), then B2c's in two columns.
 * Only a question with more than 4 answers ever reaches two columns.
 */
export const B2C_TWO_COLUMNS_FROM = B2_LEVELS.length + B2C_LEVELS.length;
const LAST_ONE_COLUMN = B2C_TWO_COLUMNS_FROM - 1;
const LAST_TWO_COLUMNS = B2C_TWO_COLUMNS_FROM + B2C_LEVELS.length - 1;

/** The percent's width, kept for "100%": a share that grows never re-wraps an answer and so never moves the layout. */
const SHARE_WIDTH = 'min-w-[4.5ch] text-right';

export interface BigScreenQuestionProps {
  state: LiveState;
  question: LiveQuestion;
  reconnecting?: boolean;
}

/**
 * B2: the open question with live bars and percents on the room's screen at once (TECH-867 § Decided "One
 * count"), "Question {n} of {m} · {k} answers" under the bars, the QR at 240px. B2c, when B2 does not fit:
 * answers 24px wrapping as far as they need, the QR 228px in a side column, the count still at the bottom (L14),
 * first in one column, and in two only with more than 4 answers and when one column cannot fit.
 */
export function BigScreenQuestion({
  state,
  question,
  reconnecting = false,
}: BigScreenQuestionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stage = useStageBox();
  const fitKey = `${question.position}|${question.text}|${question.options.map(o => o.text).join('\u0000')}|${Math.round(stage.width)}x${Math.round(stage.height)}`;
  const rung = useFitLevel(
    rootRef,
    question.options.length <= ALWAYS_ONE_COLUMN_MAX_OPTIONS ? LAST_ONE_COLUMN : LAST_TWO_COLUMNS,
    fitKey,
  );

  const display = liveLinkDisplay(state.live_code);
  const options = [...question.options].sort((a, b) => a.position - b.position);
  const countLine = LIVE_POLL_COPY.questionOfCount(
    question.position + 1,
    state.questions_count,
    question.voters_count,
  );

  // The place's small logo beside the pill, at the pill's own height so it never costs a row any room.
  const liveMark = (
    <span className="flex items-center gap-2.5">
      <PlaceLogo
        name={state.place.name}
        imageUrl={state.place.image_url}
        className="h-9 w-9 rounded-lg"
        initialsClassName="text-[14px] leading-[18px]"
      />
      <LiveBadge size="screen" />
    </span>
  );

  const topBar = (
    <div className="flex shrink-0 items-center gap-5">
      {liveMark}
      {reconnecting ? <ReconnectingChip /> : null}
    </div>
  );

  const liveRow = (
    <div className="flex flex-wrap items-center gap-2 self-start">
      {liveMark}
      {reconnecting ? <ReconnectingChip /> : null}
    </div>
  );

  if (rung >= B2_LEVELS.length) {
    const twoColumns = rung >= B2C_TWO_COLUMNS_FROM;
    const level = rung - (twoColumns ? B2C_TWO_COLUMNS_FROM : B2_LEVELS.length);
    const l = B2C_LEVELS[level];
    const half = Math.ceil(options.length / 2);
    const columns = twoColumns ? [options.slice(0, half), options.slice(half)] : [options];
    const tile = (
      <div className="relative flex w-[260px] flex-col items-center gap-3 rounded-[20px] bg-white p-4">
        <BigScreenQr link={liveLink(state.live_code)} size={228} />
        <p className="w-[228px] text-center font-fira text-[20px] font-semibold leading-[26px] text-mb-ink">
          {LIVE_POLL_COPY.scanToParticipate}
          <br />
          <TypedLink display={display} />
        </p>
      </div>
    );
    const count = (
      <p
        className={cn(
          'font-fira text-[22px] font-bold leading-7 text-mb-gray-dark',
          l.countSide && 'w-[260px] text-center',
        )}
        data-testid="big-screen-count"
      >
        {countLine}
      </p>
    );
    return (
      <div
        ref={rootRef}
        className={cn(
          'relative flex h-full w-full flex-col overflow-hidden bg-mb-bg pl-16 pr-10 [&>*]:shrink-0',
          l.root,
        )}
        data-testid="big-screen-question"
        data-layout="B2c"
        data-columns={columns.length}
        data-fit-level={level}
      >
        {l.side ? null : topBar}
        <h1
          className={cn(
            'w-[896px] break-words font-livvic text-[40px] font-bold tracking-[-0.015em] text-mb-ink',
            l.q,
          )}
        >
          {question.text}
        </h1>
        <div className="flex w-[896px] gap-3.5">
          {columns.map((column, i) => (
            <div key={i} className={cn('flex min-w-0 flex-1 flex-col', l.col)}>
              {column.map(option => (
                <ShareBar
                  key={option.id}
                  label={option.text}
                  share={pollShare(option.votes_count, question.voters_count)}
                  className={cn('rounded-xl', l.row)}
                  labelClassName={cn('text-[24px]', l.text)}
                  shareClassName={cn('text-[24px]', SHARE_WIDTH, l.text)}
                />
              ))}
            </div>
          ))}
        </div>
        {l.countSide ? null : count}
        {l.side ? (
          <div className="absolute right-10 top-4 flex w-[260px] flex-col items-center gap-3">
            {liveRow}
            {tile}
            {l.countSide ? count : null}
          </div>
        ) : (
          <div className="absolute right-10 top-[151px]">{tile}</div>
        )}
      </div>
    );
  }

  const l = B2_LEVELS[rung];
  const b2Tile = (
    <div className="relative flex w-[320px] flex-col items-center gap-2.5 rounded-[20px] bg-white p-4">
      <BigScreenQr link={liveLink(state.live_code)} size={240} />
      <p className="text-center font-fira text-[20px] font-semibold leading-[26px] text-mb-ink">
        {LIVE_POLL_COPY.scanToParticipate}
        <br />
        <TypedLink display={display} />
      </p>
    </div>
  );
  return (
    <div
      ref={rootRef}
      className={cn(
        'relative flex h-full w-full flex-col overflow-hidden bg-mb-bg px-[88px] [&>*]:shrink-0',
        l.root,
      )}
      data-testid="big-screen-question"
      data-layout="B2"
      data-columns={1}
      data-fit-level={rung}
    >
      {l.side ? null : topBar}
      <h1
        className={cn(
          'w-[790px] break-words font-livvic font-bold tracking-[-0.015em] text-mb-ink',
          l.q,
        )}
      >
        {question.text}
      </h1>
      <div className={cn('flex w-[790px] flex-col', l.bars)}>
        {options.map(option => (
          <ShareBar
            key={option.id}
            label={option.text}
            share={pollShare(option.votes_count, question.voters_count)}
            className={cn('gap-4 rounded-xl px-6', l.row)}
            labelClassName="text-[28px] leading-8"
            shareClassName={cn('text-[28px] leading-8', SHARE_WIDTH)}
          />
        ))}
      </div>
      {l.side ? null : (
        <p
          className="font-fira text-[24px] font-bold leading-7 text-mb-gray-dark"
          data-testid="big-screen-count"
        >
          {countLine}
        </p>
      )}
      {l.side ? (
        <div className="absolute right-10 top-4 flex w-[320px] flex-col items-center gap-3">
          {liveRow}
          {b2Tile}
          <p
            className="text-center font-fira text-[24px] font-bold leading-7 text-mb-gray-dark"
            data-testid="big-screen-count"
          >
            {countLine}
          </p>
        </div>
      ) : (
        <div className="absolute right-10 top-14">{b2Tile}</div>
      )}
    </div>
  );
}
